import React, { useEffect, useState } from "react";
import { z } from "zod";
import { api, extractApiError } from "@/lib/api";
import { useT } from "@/lib/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { PageLoadError } from "./NoAccess";
import { Loader2, Save, Plus, Trash2, X } from "lucide-react";
// Relative, not "@/...": validation infrastructure, resolved for REAL under Jest (no "@"
// alias there) — see lib/validation.js / hooks/useFieldErrors.js headers. Used directly
// (not the useFieldErrors hook) because this screen holds MANY independent edit rows —
// one SKU/booster card per key — never a single flat form the hook's one-map shape fits.
import { validateWith, requiredField } from "../lib/validation";
import FieldError, { invalidInputClass } from "../components/ui/FieldError";
import MediaPickerField from "./site/MediaPickerField";
import { OPERATING_CURRENCIES, currencyLabel } from "../lib/currencies";

// Shape: `{usd: 0, cad: 0}` — one zeroed entry per operating currency, keyed lowercase
// like every stored grid (the backend's `_DEFAULT_PRICE_GRID` builds the same thing).
const emptyPrice = () => Object.fromEntries(OPERATING_CURRENCIES.map((c) => [c, 0]));

// The backend's `SkuCreate.category` literal, in its order; each value is labelled
// `cat.category.<value>`. A stored value outside it shows raw rather than as a blank label.
export const SKU_CATEGORIES = ["install", "data_porting"];

// Mirrors the backend's own hard rule for these two catalog shapes
// (super_admin_routes.py sa_create_sku / sa_create_booster: "sku_id and name are
// required" / "booster_id and name are required"). `_id` is read-only once a row exists
// — never an editable field in these cards — but is validated too: a defensive check
// costs nothing and keeps the schema an honest mirror of the shape the backend accepts.
const skuSchema = z.object({ sku_id: requiredField(64), name: requiredField(200) });
const boosterSchema = z.object({ booster_id: requiredField(64), name: requiredField(200) });

export default function CatalogManager() {
  const t = useT("superadmin");
  const tc = useT("common");
  const [skus, setSkus] = useState([]);
  const [boosters, setBoosters] = useState([]);
  const [drafts, setDrafts] = useState({});
  // Field errors per draft key ("sku-<id>" / "b-<id>") — namespaced the same way `drafts`
  // already is, since each card is its own independent save, not one shared form.
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [tab, setTab] = useState("skus");
  const [newSku, setNewSku] = useState(null);
  const [newBooster, setNewBooster] = useState(null);

  const clearError = (key, field) => {
    setErrors((p) => {
      if (!p[key]?.[field]) return p;
      const next = { ...p, [key]: { ...p[key] } };
      delete next[key][field];
      return next;
    });
  };

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    let s;
    let b;
    try {
      [s, b] = await Promise.all([
        api.get("/super-admin/skus"),
        api.get("/super-admin/boosters"),
      ]);
    } catch (e) {
      setLoadError(e);
      setLoading(false);
      return;
    }
    setSkus(s.data);
    setBoosters(b.data);
    const d = {};
    s.data.forEach((x) => (d[`sku-${x.sku_id}`] = { ...x }));
    b.data.forEach((x) => (d[`b-${x.booster_id}`] = { ...x }));
    setDrafts(d);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const saveSku = async (sku) => {
    const key = `sku-${sku.sku_id}`;
    const d = drafts[key];
    const result = validateWith(skuSchema, { sku_id: d.sku_id, name: d.name });
    if (result) { setErrors((p) => ({ ...p, [key]: result })); toast.error(tc("validation.fixHighlighted")); return; }
    setErrors((p) => ({ ...p, [key]: null }));
    try {
      await api.put(`/super-admin/skus/${sku.sku_id}`, {
        name: d.name, price: d.price, features: d.features, tagline: d.tagline, active: d.active,
      });
      toast.success(t("cat.skuUpdated"));
      load();
    } catch (e) { toast.error(extractApiError(e, t("cat.error"), t)); }
  };

  const saveBooster = async (b) => {
    const key = `b-${b.booster_id}`;
    const d = drafts[key];
    const result = validateWith(boosterSchema, { booster_id: d.booster_id, name: d.name });
    if (result) { setErrors((p) => ({ ...p, [key]: result })); toast.error(tc("validation.fixHighlighted")); return; }
    setErrors((p) => ({ ...p, [key]: null }));
    try {
      await api.put(`/super-admin/boosters/${b.booster_id}`, {
        name: d.name, monthly_price: d.monthly_price, features: d.features, active: d.active,
        image_media_id: d.image_media_id || null,
      });
      toast.success(t("cat.boosterUpdated"));
      load();
    } catch (e) { toast.error(extractApiError(e, t("cat.error"), t)); }
  };

  const createSku = async () => {
    if (!newSku?.sku_id?.trim() || !newSku?.name?.trim()) return toast.error(t("cat.skuIdNameReq"));
    try {
      await api.post("/super-admin/skus", newSku);
      toast.success(t("cat.skuCreated"));
      setNewSku(null);
      load();
    } catch (e) { toast.error(extractApiError(e, t("cat.error"), t)); }
  };

  const createBooster = async () => {
    if (!newBooster?.booster_id?.trim() || !newBooster?.name?.trim()) return toast.error(t("cat.boosterIdNameReq"));
    try {
      await api.post("/super-admin/boosters", newBooster);
      toast.success(t("cat.boosterCreated"));
      setNewBooster(null);
      load();
    } catch (e) { toast.error(extractApiError(e, t("cat.error"), t)); }
  };

  const deleteSku = async (sku_id) => {
    if (!window.confirm(t("cat.confirmDeleteSku", { id: sku_id }))) return;
    try { await api.delete(`/super-admin/skus/${sku_id}`); toast.success(t("cat.skuDeleted")); load(); }
    catch (e) { toast.error(extractApiError(e, t("cat.error"), t)); }
  };

  const deleteBooster = async (booster_id) => {
    if (!window.confirm(t("cat.confirmDeleteBooster", { id: booster_id }))) return;
    try { await api.delete(`/super-admin/boosters/${booster_id}`); toast.success(t("cat.boosterDeleted")); load(); }
    catch (e) { toast.error(extractApiError(e, t("cat.error"), t)); }
  };

  const setPrice = (key, cur, val, field = "price") => {
    setDrafts((p) => ({ ...p, [key]: { ...p[key], [field]: { ...(p[key]?.[field] || {}), [cur]: parseFloat(val) || 0 } } }));
  };

  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-amber-600" />;
  if (loadError) return <PageLoadError error={loadError} onRetry={load} testId="catalog-load-error" />;

  return (
    <div data-testid="catalog-manager">
      <h1 className="font-sol font-semibold text-4xl mb-2">{t("cat.title")}</h1>
      <p className="text-slate-500 mb-8">{t("cat.subtitle")}</p>

      <div className="flex gap-2 mb-8 border-b border-slate-100">
        {[
          { k: "skus", l: t("cat.tabSkus") },
          { k: "boosters", l: t("cat.tabBoosters") },
        ].map((tb) => (
          <button
            key={tb.k}
            onClick={() => setTab(tb.k)}
            data-testid={`tab-${tb.k}`}
            className={`px-4 py-2 text-sm border-b-2 ${tab === tb.k ? "border-amber-300 text-amber-600" : "border-transparent text-slate-500 hover:text-slate-800"}`}
          >{tb.l}</button>
        ))}
      </div>

      {tab === "skus" && (
        <>
          <div className="mb-5">
            {newSku ? (
              <div className="bg-slate-50 border border-amber-200 rounded-2xl p-5" data-testid="new-sku-form">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-amber-600">{t("cat.newSku")}</span>
                  <button onClick={() => setNewSku(null)}><X className="w-4 h-4 text-slate-400" /></button>
                </div>
                <div className="grid sm:grid-cols-2 gap-3 mb-3">
                  <Input placeholder={t("cat.phSkuId")} value={newSku.sku_id} onChange={(e) => setNewSku({ ...newSku, sku_id: e.target.value })} className="bg-white shadow-sm border-slate-200 text-slate-800" data-testid="new-sku-id" />
                  <Input placeholder={t("cat.phName")} value={newSku.name} onChange={(e) => setNewSku({ ...newSku, name: e.target.value })} className="bg-white shadow-sm border-slate-200 text-slate-800" data-testid="new-sku-name" />
                  <select value={newSku.category} onChange={(e) => setNewSku({ ...newSku, category: e.target.value })} className="bg-white shadow-sm border border-slate-200 text-slate-800 rounded-md px-3 h-10 text-sm" data-testid="new-sku-category">
                    {SKU_CATEGORIES.map((c) => (
                      <option key={c} value={c} className="bg-white">{t(`cat.category.${c}`)}</option>
                    ))}
                  </select>
                  <Input placeholder={t("cat.phTagline")} value={newSku.tagline} onChange={(e) => setNewSku({ ...newSku, tagline: e.target.value })} className="bg-white shadow-sm border-slate-200 text-slate-800" />
                </div>
                <div className="grid grid-cols-4 gap-2 mb-3">
                  {OPERATING_CURRENCIES.map((c) => (
                    <div key={c} className="flex items-center gap-1">
                      <span className="text-xs text-slate-400 w-8">{currencyLabel(c)}</span>
                      <Input type="number" value={newSku.price[c]} onChange={(e) => setNewSku({ ...newSku, price: { ...newSku.price, [c]: parseFloat(e.target.value) || 0 } })} className="bg-white shadow-sm border-slate-200 text-slate-800 text-sm" data-testid={`new-sku-price-${c}`} />
                    </div>
                  ))}
                </div>
                <Button onClick={createSku} data-testid="create-sku-btn" size="sm" className="bg-amber-300 hover:bg-amber-200 text-black rounded-full"><Plus className="w-3 h-3 mr-1" /> {t("cat.createSku")}</Button>
              </div>
            ) : (
              <Button onClick={() => setNewSku({ sku_id: "", name: "", category: "install", tagline: "", price: emptyPrice() })} data-testid="new-sku" className="bg-amber-300 hover:bg-amber-200 text-black rounded-full"><Plus className="w-4 h-4 mr-1" /> {t("cat.newSku")}</Button>
            )}
          </div>
          <div className="grid lg:grid-cols-2 gap-5">
            {skus.map((s) => {
              const d = drafts[`sku-${s.sku_id}`] || s;
              return (
                <div key={s.sku_id} data-testid={`sa-sku-${s.sku_id}`} className="bg-white border border-slate-200 rounded-2xl p-5">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs text-amber-600 uppercase tracking-wider" data-testid={`sku-category-${s.sku_id}`}>
                      {SKU_CATEGORIES.includes(s.category) ? t(`cat.category.${s.category}`) : s.category}
                    </span>
                    <div className="flex items-center gap-2">
                      {s.featured && <span className="text-xs bg-amber-300/20 text-amber-600 px-2 py-0.5 rounded" data-testid={`sku-featured-${s.sku_id}`}>★ {t("cat.featured")}</span>}
                      <button onClick={() => deleteSku(s.sku_id)} data-testid={`sku-del-${s.sku_id}`} className="text-rose-400 hover:text-rose-600"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                  <Input
                    value={d.name || ""}
                    onChange={(e) => {
                      const key = `sku-${s.sku_id}`;
                      setDrafts((p) => ({ ...p, [key]: { ...p[key], name: e.target.value } }));
                      clearError(key, "name");
                    }}
                    className={`bg-white shadow-sm text-slate-800 mb-1 ${errors[`sku-${s.sku_id}`]?.name ? invalidInputClass : "border-slate-200"}`}
                    data-testid={`sku-name-${s.sku_id}`}
                  />
                  <FieldError
                    message={errors[`sku-${s.sku_id}`]?.name ? tc(errors[`sku-${s.sku_id}`].name) : ""}
                    testId={`sku-name-${s.sku_id}-error`}
                    className="mb-2"
                  />
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    {OPERATING_CURRENCIES.map((c) => (
                      <div key={c} className="flex items-center gap-2">
                        <span className="text-xs text-slate-400 w-10">{currencyLabel(c)}</span>
                        <Input type="number" value={d.price?.[c] || 0} onChange={(e) => setPrice(`sku-${s.sku_id}`, c, e.target.value)} className="bg-white shadow-sm border-slate-200 text-slate-800 text-sm" data-testid={`sku-price-${s.sku_id}-${c}`} />
                      </div>
                    ))}
                  </div>
                  <Button onClick={() => saveSku(s)} data-testid={`sku-save-${s.sku_id}`} size="sm" className="bg-amber-300 hover:bg-amber-200 text-black rounded-full w-full">
                    <Save className="w-3 h-3 mr-1" /> {t("cat.save")}
                  </Button>
                </div>
              );
            })}
          </div>
        </>
      )}

      {tab === "boosters" && (
        <>
          <div className="mb-5">
            {newBooster ? (
              <div className="bg-slate-50 border border-amber-200 rounded-2xl p-5" data-testid="new-booster-form">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-amber-600">{t("cat.newBooster")}</span>
                  <button onClick={() => setNewBooster(null)}><X className="w-4 h-4 text-slate-400" /></button>
                </div>
                <div className="grid sm:grid-cols-2 gap-3 mb-3">
                  <Input placeholder={t("cat.phBoosterId")} value={newBooster.booster_id} onChange={(e) => setNewBooster({ ...newBooster, booster_id: e.target.value })} className="bg-white shadow-sm border-slate-200 text-slate-800" data-testid="new-booster-id" />
                  <Input placeholder={t("cat.phName")} value={newBooster.name} onChange={(e) => setNewBooster({ ...newBooster, name: e.target.value })} className="bg-white shadow-sm border-slate-200 text-slate-800" data-testid="new-booster-name" />
                </div>
                <div className="grid grid-cols-4 gap-2 mb-3">
                  {OPERATING_CURRENCIES.map((c) => (
                    <div key={c} className="flex items-center gap-1">
                      <span className="text-xs text-slate-400 w-8">{currencyLabel(c)}</span>
                      <Input type="number" value={newBooster.monthly_price[c]} onChange={(e) => setNewBooster({ ...newBooster, monthly_price: { ...newBooster.monthly_price, [c]: parseFloat(e.target.value) || 0 } })} className="bg-white shadow-sm border-slate-200 text-slate-800 text-sm" />
                    </div>
                  ))}
                </div>
                <Button onClick={createBooster} data-testid="create-booster-btn" size="sm" className="bg-amber-300 hover:bg-amber-200 text-black rounded-full"><Plus className="w-3 h-3 mr-1" /> {t("cat.createBooster")}</Button>
              </div>
            ) : (
              <Button onClick={() => setNewBooster({ booster_id: "", name: "", monthly_price: emptyPrice() })} data-testid="new-booster" className="bg-amber-300 hover:bg-amber-200 text-black rounded-full"><Plus className="w-4 h-4 mr-1" /> {t("cat.newBooster")}</Button>
            )}
          </div>
          <div className="grid lg:grid-cols-3 gap-5">
            {boosters.map((b) => {
              const d = drafts[`b-${b.booster_id}`] || b;
              return (
                <div key={b.booster_id} data-testid={`sa-booster-${b.booster_id}`} className="bg-white border border-slate-200 rounded-2xl p-5">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs text-slate-400">{b.booster_id}</span>
                    <div className="flex items-center gap-2">
                      {b.active === false && <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded" data-testid={`booster-withdrawn-${b.booster_id}`}>{t("cat.notForSale")}</span>}
                      <button onClick={() => deleteBooster(b.booster_id)} data-testid={`booster-del-${b.booster_id}`} className="text-rose-400 hover:text-rose-600"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                  <Input
                    value={d.name || ""}
                    onChange={(e) => {
                      const key = `b-${b.booster_id}`;
                      setDrafts((p) => ({ ...p, [key]: { ...p[key], name: e.target.value } }));
                      clearError(key, "name");
                    }}
                    className={`bg-white shadow-sm text-slate-800 mb-1 ${errors[`b-${b.booster_id}`]?.name ? invalidInputClass : "border-slate-200"}`}
                    data-testid={`booster-name-${b.booster_id}`}
                  />
                  <FieldError
                    message={errors[`b-${b.booster_id}`]?.name ? tc(errors[`b-${b.booster_id}`].name) : ""}
                    testId={`booster-name-${b.booster_id}-error`}
                    className="mb-2"
                  />
                  <div className="mb-3">
                    {/* The booster's image in the platform media library (contract §3.5):
                        `image_media_id` is stored, `image_url` only previews it. */}
                    <MediaPickerField
                      label={t("cat.boosterImage")}
                      value={d.image_media_id ? { media_id: d.image_media_id, url: d.image_url || "" } : null}
                      onChange={(picked) => setDrafts((p) => ({
                        ...p,
                        [`b-${b.booster_id}`]: { ...p[`b-${b.booster_id}`], image_media_id: picked.media_id, image_url: picked.url },
                      }))}
                      onClear={() => setDrafts((p) => ({
                        ...p,
                        [`b-${b.booster_id}`]: { ...p[`b-${b.booster_id}`], image_media_id: null, image_url: "" },
                      }))}
                      testId={`sa-booster-image-${b.booster_id}`}
                    />
                  </div>
                  <div className="space-y-2 mb-3">
                    {OPERATING_CURRENCIES.map((c) => (
                      <div key={c} className="flex items-center gap-2">
                        <span className="text-xs text-slate-400 w-10">{currencyLabel(c)}</span>
                        <Input type="number" value={d.monthly_price?.[c] || 0} onChange={(e) => setPrice(`b-${b.booster_id}`, c, e.target.value, "monthly_price")} className="bg-white shadow-sm border-slate-200 text-slate-800 text-sm" />
                        <span className="text-xs text-slate-400" data-testid="booster-per-month">{t("cat.perMonth")}</span>
                      </div>
                    ))}
                  </div>
                  {/* `active: false` withdraws the booster from sale (checkout refuses it, the
                      tenant and public catalogs hide it, current payers' lines turn retired);
                      it is what the delete refusal BOOSTER_HAS_PAYERS tells the operator to do. */}
                  <label className="flex items-center justify-between gap-3 mb-3 text-sm text-slate-700">
                    <span>{t("cat.onSale")}</span>
                    <Switch
                      checked={d.active !== false}
                      onCheckedChange={(on) => setDrafts((p) => ({ ...p, [`b-${b.booster_id}`]: { ...p[`b-${b.booster_id}`], active: on } }))}
                      data-testid={`booster-active-${b.booster_id}`}
                    />
                  </label>
                  <Button onClick={() => saveBooster(b)} data-testid={`booster-save-${b.booster_id}`} size="sm" className="bg-amber-300 hover:bg-amber-200 text-black rounded-full w-full">
                    <Save className="w-3 h-3 mr-1" /> {t("cat.save")}
                  </Button>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
